import React, { useContext, useMemo, useRef, useState, useEffect } from 'react';
import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import { Dropdown, List, ListItem, Search } from '@silverfort/sf-components';
import {
    AGGREGATION_FIELD_TO_OPTIONS,
    AGGREGATION_FIELD_TO_PARSER,
    INCIDENT_AGGREGATION_OPTIONS
} from './FilterOptions';
import useGetRefSize from '../../../../hooks/useGetRefSize';
import useGetIncidentsAggregationOverTime from '../../api/queries/useGetIncidentsAggregationOverTime';
import ItdrContext from '../../ItdrContext';
import { xAxisFormatter } from './GraphBuilder';
import { INCIDENT_TYPE } from '@silverfort-nodelibs/common/utils/consts';
import { getEnumLabelByValue } from '@silverfort-nodelibs/common/utils/common';
import GraphTooltip from './GraphTooltip';
import { flatMap, sortBy, uniq } from 'lodash';
import { subDays, getTime, startOfDay } from 'date-fns';

const BASE_STROKE_COLOR = 0xff115b;

const IncidentsGraph = () => {
    const { lastActivityDiffInDays, hiddenTypes, graphRefreshToken } = useContext(ItdrContext);
    const [aggregatedField, setAggregatedField] = useState<string>(INCIDENT_AGGREGATION_OPTIONS[0].value!);
    const [searchedAggregationOption, setSearchedAggregationOption] = useState<string | undefined>();
    const [selectedAggregationOptions, setSelectedAggregationOptions] = useState<string[]>([]);

    const graphRef = useRef(null);
    const { height: graphHeight, width: graphWidth } = useGetRefSize(graphRef);

    const { data: incidentsTimeline, isFetching } = useGetIncidentsAggregationOverTime({
        diffInDays: lastActivityDiffInDays ?? 0,
        aggregationField: aggregatedField,
        refreshToken: graphRefreshToken
    });

    useEffect(() => {
        if (incidentsTimeline && !isFetching) {
            const optionsWithData = Object.entries(incidentsTimeline)
                .filter(([, seriesData]) => seriesData.some((dataPoint) => dataPoint.count > 0))
                .map(([optionKey]) => optionKey);
            setSelectedAggregationOptions(optionsWithData);
        }
    }, [incidentsTimeline, isFetching]);

    const onAggregatedFieldChange = (value?: string | string[]) => {
        setAggregatedField(value as string);
    };

    const onAggregationOptionSelected = (value?: string) => {
        if (!value) return;

        setSelectedAggregationOptions((prevState) => {
            if (prevState.some((selectedValue) => selectedValue === value)) {
                return prevState.filter((previousSelected) => previousSelected !== value);
            }
            return [...prevState, value];
        });
    };

    const calculateTickInterval = (rangeInDays: number) => {
        if (rangeInDays === 30) return 3; // Show every 3rd tick for 30 days
        return 1;
    };

    const aggregationOptions = useMemo(() => {
        const hiddenTypesLabels = hiddenTypes?.map((type) => getEnumLabelByValue(INCIDENT_TYPE, type)) ?? [];

        return AGGREGATION_FIELD_TO_OPTIONS[aggregatedField].filter(
            (option) =>
                option.title.toLowerCase().includes(searchedAggregationOption?.toLowerCase() ?? '') &&
                !hiddenTypesLabels.includes(option.value)
        );
    }, [aggregatedField, searchedAggregationOption, hiddenTypes]);

    const allDates = useMemo(() => {
        if (!incidentsTimeline) return [];

        // Collect all dates that have data in the incidents timeline
        const allDates = flatMap(Object.values(incidentsTimeline), (series) => series.map((point) => point.date));
        return sortBy(uniq(allDates));
    }, [incidentsTimeline]);

    const createSingleDateRange = (singleDate: Record<string, any>, lastActivityDiffInDays: number) => {
        const now = new Date();

        const startPoint: Record<string, number> = {
            date: getTime(startOfDay(subDays(now, lastActivityDiffInDays))),
            ...Object.fromEntries(Object.keys(incidentsTimeline!).map((key) => [key, 0]))
        };

        const endPoint: Record<string, number> = {
            date: getTime(startOfDay(now)),
            ...Object.fromEntries(Object.keys(incidentsTimeline!).map((key) => [key, 0]))
        };

        return [startPoint, singleDate, endPoint];
    };

    const mergedData = useMemo(() => {
        if (!incidentsTimeline) return [];

        const baseData = allDates.map((date) => ({
            date,
            ...Object.fromEntries(
                Object.entries(incidentsTimeline).map(([aggKey, series]) => [
                    aggKey,
                    series.find((p) => p.date === date)?.count ?? 0
                ])
            )
        }));

        // If there's only one date, create a proper range around it
        if (baseData.length === 1 && lastActivityDiffInDays) {
            return createSingleDateRange(baseData[0], lastActivityDiffInDays);
        }

        return baseData;
    }, [incidentsTimeline, allDates, lastActivityDiffInDays]);

    const getSingleDateTicks = (singleDate: number, lastActivityDiffInDays: number) => {
        const now = new Date();

        return [getTime(startOfDay(subDays(now, lastActivityDiffInDays))), singleDate, getTime(startOfDay(now))];
    };

    const ticks = useMemo(() => {
        if (!allDates.length || !lastActivityDiffInDays) return [];

        // If there's only one date, show start, middle, and end ticks
        if (allDates.length === 1) {
            return getSingleDateTicks(allDates[0], lastActivityDiffInDays);
        }

        const tickInterval = calculateTickInterval(lastActivityDiffInDays);
        return allDates.filter((_, index) => index % tickInterval === 0);
    }, [allDates, lastActivityDiffInDays]);

    const lines = useMemo(() => {
        return selectedAggregationOptions.map((aggKey, index) => {
            const parsedFieldValue = AGGREGATION_FIELD_TO_PARSER[aggregatedField]?.(aggKey);

            const strokeColor =
                parsedFieldValue?.color ??
                `#${((index + 1) * BASE_STROKE_COLOR).toString(16).toUpperCase().slice(0, 6)}`;

            return (
                <Line
                    key={aggKey}
                    dataKey={aggKey}
                    name={parsedFieldValue?.label ?? aggKey}
                    stroke={strokeColor}
                    strokeWidth={2}
                    dot={false}
                />
            );
        });
    }, [incidentsTimeline, aggregatedField, selectedAggregationOptions]);

    return (
        <div className="w-full h-full flex rounded-xl border">
            <div className="h-full w-[90%] flex flex-col gap-6 p-4">
                <div className="h-8 w-full flex items-center gap-2">
                    <div className="text-14 text-sf-gray-800 font-sf font-medium ml-[45px]">Incidents Over Time By</div>
                    <Dropdown
                        size="small"
                        variant="trigger"
                        items={INCIDENT_AGGREGATION_OPTIONS}
                        onChange={onAggregatedFieldChange}
                        selectedItems={aggregatedField}
                    />
                </div>

                <div className="w-full flex-grow flex-shrink" ref={graphRef}>
                    {!isFetching && (
                        <LineChart width={graphWidth} height={graphHeight} data={mergedData}>
                            <CartesianGrid strokeWidth={1} vertical={false} />
                            <XAxis
                                dataKey="date"
                                type="number"
                                scale="time"
                                tickFormatter={xAxisFormatter}
                                ticks={ticks}
                                domain={['auto', 'auto']}
                                tick={{ fontSize: 10 }}
                            />
                            <YAxis
                                domain={
                                    selectedAggregationOptions.length === 0
                                        ? [0, 0] //default values for empty chart
                                        : [0, 'auto']
                                }
                                className="text-sf-gray-500 text-sm"
                                tick={{ fontSize: 10 }}
                            />
                            <Tooltip
                                content={
                                    <GraphTooltip
                                        selectedAggregationOptions={selectedAggregationOptions}
                                        maxHeight={graphHeight}
                                    />
                                }
                                wrapperStyle={{ pointerEvents: 'auto' }}
                                itemSorter={(item) => -(item.value ?? 0)}
                            />
                            {lines}
                        </LineChart>
                    )}
                </div>
            </div>
            <div className="border-l w-1 flex-grow h-full flex flex-col">
                <Search
                    onKeyDown={(e) => {
                        const isEmptyList = !searchedAggregationOption?.length;
                        // MUI Menu component for some reason take the focus once we type the first letter
                        // of the first item in the list. to prevent it, we stop the keyDown propagation
                        if (
                            searchedAggregationOption?.[0]?.startsWith(e.key) ||
                            searchedAggregationOption?.[0]?.startsWith(e.key.toUpperCase())
                        ) {
                            e.stopPropagation();
                        }
                        // In order to handle the empty list message - No Results Found
                        if (isEmptyList && e.key.toLowerCase() === 'n') {
                            e.stopPropagation();
                        }
                    }}
                    autoFocus
                    width="100%"
                    className="h-10"
                    onChange={setSearchedAggregationOption}
                />
                <div className="h-[1px] w-full bg-sf-gray-400" />
                <List className="w-full flex-grow h-1 overflow-y-auto">
                    {aggregationOptions?.map((option) => (
                        <ListItem
                            key={option.value}
                            value={option.value}
                            title={option.title}
                            onCheck={onAggregationOptionSelected}
                            onClick={onAggregationOptionSelected}
                            multiSelect
                            selected={selectedAggregationOptions.includes(option?.value ?? '')}
                        />
                    ))}
                </List>
            </div>
        </div>
    );
};

export default IncidentsGraph;
